import { formatDateRange, plural } from '@trips/shared/format';
import type { LatLon } from '@trips/shared/geo';
import { diffDays } from '@trips/shared/time';
import { useMemo, useRef, useState } from 'react';
import { patch, remove } from '../../data/mutate';
import {
  selectItemsByTrip,
  selectPlaceLookup,
  selectPrefs,
  selectTimeline,
  selectTrip,
  selectTripSummaries,
  selectTripSummary,
} from '../../data/selectors';
import { getState, useStore } from '../../data/store';
import { back, navigate } from '../../lib/router';
import { DESKTOP, useMedia } from '../../lib/useMedia';
import { Button, IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { TextField } from '../../ui/Field';
import { Icon } from '../../ui/Icon';
import { ListGroup, ListRow } from '../../ui/List';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/Toast';
import { TopBar } from '../../ui/TopBar';
import { Timeline } from './Timeline';
import { TripMap } from './TripMap';
import s from './TripScreen.module.css';

type Section = 'timeline' | 'map';

/** Moves every plan of one trip into another, then removes the emptied trip. */
async function mergeTrips(intoId: string, fromId: string) {
  const { trips, items } = getState().tables;
  const into = trips.get(intoId);
  const from = trips.get(fromId);
  if (!into || !from) return;
  for (const i of items.values())
    if (i.tripId === fromId && !i.deleted)
      await patch('items', i.id, { tripId: intoId, tripLocked: true });
  // Date overrides on either trip would hide the other's days; widen them to cover both.
  const starts = [into.startDate, from.startDate].filter((d): d is string => !!d).sort();
  const ends = [into.endDate, from.endDate].filter((d): d is string => !!d).sort();
  await patch('trips', intoId, {
    pinned: true,
    startDate: into.startDate ? (starts[0] ?? null) : null,
    endDate: into.endDate ? (ends[ends.length - 1] ?? null) : null,
    notes: [into.notes, from.notes].filter(Boolean).join('\n\n'),
  });
  await remove('trips', fromId);
}

/** Days between two trips' date ranges; 0 when they overlap. */
function gap(a: { startDate: string | null; endDate: string | null }, b: typeof a): number {
  if (!a.startDate || !b.startDate) return Number.POSITIVE_INFINITY;
  const aEnd = a.endDate ?? a.startDate;
  const bEnd = b.endDate ?? b.startDate;
  if (diffDays(aEnd, b.startDate) >= 0 && diffDays(bEnd, a.startDate) >= 0) return 0;
  return Math.min(Math.abs(diffDays(aEnd, b.startDate)), Math.abs(diffDays(bEnd, a.startDate)));
}

function TripMenu({
  tripId,
  open,
  onClose,
}: {
  tripId: string;
  open: boolean;
  onClose: () => void;
}) {
  const trip = useStore((st) => selectTrip(st, tripId));
  const summary = useStore((st) => selectTripSummary(st, tripId));
  const [title, setTitle] = useState(trip?.title ?? '');
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  const all = useStore(selectTripSummaries);
  const prefs = useStore(selectPrefs);
  const nearby = useMemo(
    () =>
      summary
        ? all
            .filter((t) => t.id !== tripId && t.startDate)
            .map((t) => ({ t, gap: gap(summary, t) }))
            .sort((a, b) => a.gap - b.gap)
            .slice(0, 8)
        : [],
    [all, summary, tripId],
  );
  if (!trip || !summary) return null;
  return (
    <Sheet open={open} onClose={onClose} title="Trip">
      <div className={s.menu}>
        <TextField
          label="Name"
          value={title}
          placeholder={summary.autoTitle}
          onChange={setTitle}
          hint="Leave empty to name it after where you spend the most nights."
          onBlur={() => {
            const next = title.trim() || null;
            if (next !== trip.title) void patch('trips', tripId, { title: next });
          }}
        />
        <ListGroup>
          <ListRow
            icon="plus"
            title="Add plan"
            onClick={() => {
              onClose();
              navigate(`/new?trip=${tripId}`, { overlay: true });
            }}
          />
          <ListRow
            icon="arrows-merge"
            title="Merge with another trip"
            onClick={() => setMerging(!merging)}
          />
          {merging &&
            nearby.map(({ t, gap: days }) => (
              <ListRow
                key={t.id}
                icon="suitcase-rolling"
                title={t.title}
                detail={`${t.startDate ? formatDateRange(t.startDate, t.endDate ?? t.startDate, prefs) : ''}${
                  days === 0 ? ', overlaps' : `, ${plural(days, 'day')} apart`
                }`}
                onClick={async () => {
                  await mergeTrips(tripId, t.id);
                  setMerging(false);
                  onClose();
                  toast(`Merged ${t.title} into this trip`);
                }}
              />
            ))}
          {confirming ? (
            <ListRow
              icon="trash-simple"
              danger
              title="Delete this trip and its plans"
              detail="This can't be undone from the app."
              onClick={async () => {
                const items = (await import('../../data/store')).getState().tables.items;
                for (const i of items.values())
                  if (i.tripId === tripId && !i.deleted) await remove('items', i.id);
                await remove('trips', tripId);
                onClose();
                toast('Trip deleted');
                navigate('/', { replace: true, transition: 'pop' });
              }}
            />
          ) : (
            <ListRow
              icon="trash-simple"
              danger
              title="Delete trip"
              onClick={() => setConfirming(true)}
            />
          )}
        </ListGroup>
      </div>
    </Sheet>
  );
}

export function TripScreen({ tripId, pane }: { tripId: string; pane?: boolean }) {
  const desktop = useMedia(DESKTOP);
  const trip = useStore((st) => selectTrip(st, tripId));
  const summary = useStore((st) => selectTripSummary(st, tripId));
  const timeline = useStore((st) => selectTimeline(st, tripId));
  const items = useStore((st) => selectItemsByTrip(st).get(tripId));
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const [section, setSection] = useState<Section>('timeline');
  const [menu, setMenu] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const _stops = useMemo(() => {
    const out: LatLon[] = [];
    for (const i of items ?? []) {
      if (i.type !== 'lodging') continue;
      const p = places(i.startPlaceId);
      if (p?.lat != null && p.lon != null) out.push({ lat: p.lat, lon: p.lon });
    }
    return out;
  }, [items, places]);

  if (!trip || trip.deleted || !summary) {
    return (
      <div className={s.missing}>
        <TopBar backTo="/" pane={pane} />
        <EmptyState
          icon="suitcase-rolling"
          title="This trip isn't here"
          action={<Button onClick={() => back('/')}>Back to trips</Button>}
        >
          <p>It may have been deleted on another device.</p>
        </EmptyState>
      </div>
    );
  }

  const stickyTop = pane || desktop ? '56px' : 'calc(56px + var(--safe-top))';

  return (
    <div className={`${s.screen} ${pane ? s.pane : ''}`}>
      <TopBar
        title={summary.title}
        backTo={pane ? false : '/'}
        watch={titleRef}
        pane={pane}
        actions={
          <IconButton icon="dots-three" label="Trip options" onClick={() => setMenu(true)} />
        }
      />
      <div className={s.head}>
        <h1 ref={titleRef} className="t-title1" style={{ viewTransitionName: `title-${tripId}` }}>
          {summary.title}
        </h1>
        <p className={s.meta}>
          {summary.startDate && summary.endDate
            ? formatDateRange(summary.startDate, summary.endDate, prefs)
            : 'No dates yet'}
          {summary.days > 0 && `, ${plural(summary.days, 'day')}`}
        </p>
      </div>

      <div className={s.sections}>
        {(
          [
            ['timeline', 'Plans'],
            ['map', 'Map'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={s.pill}
            aria-pressed={section === value}
            onClick={() => setSection(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={s.body}>
        {section === 'timeline' &&
          (timeline.rows.length || timeline.unscheduled.length ? (
            <Timeline
              model={timeline}
              tripId={tripId}
              stickyTop={stickyTop}
              unscheduled={timeline.unscheduled}
            />
          ) : (
            <EmptyState
              icon="calendar-blank"
              title="No plans yet"
              action={
                <Button
                  variant="primary"
                  icon="plus"
                  onClick={() => navigate(`/new?trip=${tripId}`, { overlay: true })}
                >
                  Add plan
                </Button>
              }
            >
              <p>Add a flight, stay, car or reservation to build the timeline.</p>
            </EmptyState>
          ))}
        {section === 'map' && <TripMap items={items ?? []} />}
      </div>

      {!pane && (
        <button
          type="button"
          className={s.fab}
          onClick={() => navigate(`/new?trip=${tripId}`, { overlay: true })}
        >
          <Icon name="plus" size={20} />
          <span>Add plan</span>
        </button>
      )}
      <TripMenu key={String(menu)} tripId={tripId} open={menu} onClose={() => setMenu(false)} />
    </div>
  );
}

TripScreen.Placeholder = function TripPlaceholder() {
  return (
    <div className={s.placeholder}>
      <EmptyState icon="suitcase-rolling" title="Pick a trip">
        <p>Choose a trip on the left to see its plans. Press n to add a plan.</p>
      </EmptyState>
    </div>
  );
};
